import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ObjectBounds } from '@/lib/guides/types/object-bounds'
import { type GridSnapLiveRect, snapPointToNearestGridCorner } from '@/lib/guides/grid-snap'
import { isShapeGeometryGestureBlocked } from '@/lib/groups/element-edit-policy'
import { computeGuideSnapAdjustment } from '@/lib/guides/guide-snap-delta'
import { isShapeSelectable } from '../helpers/selection-scope'
import type { InteractionState } from '../types/interaction-state'

export interface GuideSnapDeps {
  /** Reads the live shape list. */
  getShapes: () => ShapeData[]
  guides: Array<{ id: string; type: 'horizontal' | 'vertical'; position: number }>
  snapToGuides: boolean
  snapGuideDistance: number
  snapToGrid: boolean
  gridSnapStep: number
  gridSnapLiveRects: GridSnapLiveRect[]
  getMovedSelectionBounds: (
    movingShapes: Array<{ shape: ShapeData; initialX: number; initialY: number }>,
    rawDx: number,
    rawDy: number
  ) => ObjectBounds | null
}

/** A move delta snapped to ruler guides, then (optionally) to the grid. */
export function computeGuideSnappedMoveDelta(
  rawDx: number,
  rawDy: number,
  interaction: Pick<InteractionState, 'shapeId' | 'initial' | 'multiMoveInitials'>,
  /** When false, skip grid snap (fluid drag). When true, grid runs after ruler-guide snap so commit matches drag preview. */
  options: { applyGridSnap?: boolean } | undefined,
  {
    getShapes,
    guides,
    snapToGuides,
    snapGuideDistance,
    snapToGrid,
    gridSnapStep,
    gridSnapLiveRects,
    getMovedSelectionBounds,
  }: GuideSnapDeps
): { dx: number; dy: number } {
  const applyGridSnap = options?.applyGridSnap !== false
  const movingShapes: Array<{ shape: ShapeData; initialX: number; initialY: number }> = []

  if (interaction.multiMoveInitials) {
    for (const [id, initialPos] of Object.entries(interaction.multiMoveInitials)) {
      const shape =
        getShapes().find((candidate) => candidate.id === id) ??
        (interaction.shapeId === id ? interaction.initial : undefined)

      if (shape) {
        movingShapes.push({
          shape,
          initialX: initialPos.x,
          initialY: initialPos.y,
        })
      }
    }
  } else {
    const shape =
      getShapes().find((candidate) => candidate.id === interaction.shapeId) ?? interaction.initial

    if (shape) {
      movingShapes.push({
        shape,
        initialX: interaction.initial.x,
        initialY: interaction.initial.y,
      })
    }
  }

  if (movingShapes.length === 0) {
    return { dx: rawDx, dy: rawDy }
  }

  let dx = rawDx
  let dy = rawDy

  /*
   * Ruler guides first, then optional grid snap when `applyGridSnap` is true (pointer-up /
   * nudge). Live drag passes `applyGridSnap: false` so the preview matches guide + smart
   * guides only; if grid ran first and returned early, commit would ignore guides and the
   * shape would jump off a visible guide snap (BUG: corner snap on release).
   */
  if (snapToGuides && guides.length > 0) {
    const movingSelectionBounds = getMovedSelectionBounds(movingShapes, rawDx, rawDy)

    if (!movingSelectionBounds) {
      return { dx: rawDx, dy: rawDy }
    }

    const { adjustX, adjustY } = computeGuideSnapAdjustment(
      movingSelectionBounds,
      guides,
      snapGuideDistance
    )

    dx = rawDx + adjustX
    dy = rawDy + adjustY
  }

  if (snapToGrid && applyGridSnap) {
    const step = Math.max(1, Math.round(Number(gridSnapStep)) || 1)
    const movingSelectionBounds = getMovedSelectionBounds(movingShapes, dx, dy)

    if (!movingSelectionBounds) {
      return { dx, dy }
    }

    /*
     * Snap path geometry (not stroke) to the nearest grid corner on one page, using the
     * selection bounds after ruler-guide adjustment when applicable.
     */
    const pathXRef =
      rawDx <= 0 ? movingSelectionBounds.x : movingSelectionBounds.x + movingSelectionBounds.width
    const pathYRef = movingSelectionBounds.y

    if (Number.isFinite(pathXRef) && Number.isFinite(pathYRef)) {
      const snapped = snapPointToNearestGridCorner(pathXRef, pathYRef, step, gridSnapLiveRects)

      dx += snapped.x - pathXRef
      dy += snapped.y - pathYRef
    }
  }

  return { dx, dy }
}

/** Keyboard nudge: every movable selected shape shifted by exactly the requested step. */
export function computeSnappedNudgeItems(
  rawDx: number,
  rawDy: number,
  {
    shapes,
    selectedIds,
    editScopeGroupId,
  }: { shapes: ShapeData[]; selectedIds: string[]; editScopeGroupId: string | null }
): Array<{
  shapeId: string
  before: { x: number; y: number }
  after: { x: number; y: number }
}> {
  const list = shapes
  const byId = new Map(list.map((shape) => [shape.id, shape]))
  const selected = list.filter(
    (shape) =>
      selectedIds.includes(shape.id) &&
      isShapeSelectable(shape, editScopeGroupId) &&
      !isShapeGeometryGestureBlocked(shape, byId)
  )

  if (selected.length === 0) {
    return []
  }

  // Nudge always moves by exactly the requested step — no guide or smart-guide snap.
  // Guide snapping would hold the shape in place once it aligns with a nearby guide,
  // causing subsequent arrow-key presses to produce zero net movement.
  return selected.map((shape) => ({
    shapeId: shape.id,
    before: { x: shape.x, y: shape.y },
    after: {
      x: shape.x + rawDx,
      y: shape.y + rawDy,
    },
  }))
}
